import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// "Meet your coach" — photo + credentials promo (15s).
// Records at 1080x1920 via /app/scripts/record_promo.py --url .../promo2

const PHOTOS = {
  hero: "https://customer-assets.emergentagent.com/job_slay-aesthetic-boost/artifacts/5mpzbdp8_sweat_with_slay_7.png",
  about: "https://customer-assets.emergentagent.com/job_slay-aesthetic-boost/artifacts/nb2spi5w_IMG_8014%20%281%29.jpg",
  social: "https://customer-assets.emergentagent.com/job_slay-aesthetic-boost/artifacts/c7xf6eol_CEBC811F-CCAB-4A09-A66E-6D8B9203D7F0.jpeg",
};

const SAFE_TOP = 360;
const SAFE_BOTTOM = 580;

// Reusable: full-bleed photo (covers entire frame, ignores safe band)
const BgPhoto = ({ src, kenBurns = "in" }) => (
  <motion.img
    src={src}
    alt=""
    initial={{ scale: kenBurns === "in" ? 1.0 : 1.12, opacity: 0 }}
    animate={{ scale: kenBurns === "in" ? 1.12 : 1.0, opacity: 1 }}
    transition={{ duration: 3.0, ease: "linear" }}
    className="absolute inset-0 w-full h-full object-cover"
  />
);

const SLIDES = [
  {
    id: "hook",
    duration: 2400,
    bg: "#0A0A0A",
    full: true,
    render: () => (
      <>
        <BgPhoto src={PHOTOS.hero} kenBurns="in" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/55 to-black/85" />
        <div className="absolute inset-0 flex flex-col justify-end pb-[640px] px-16">
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-[#FF1B91] uppercase tracking-[0.5em] text-sm font-bold mb-6">
            / Meet your coach
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="font-display font-black uppercase text-white leading-[0.85] tracking-tighter"
            style={{ fontSize: "180px" }}
          >
            AJ <span className="font-brush font-normal normal-case text-[#FF1B91] -rotate-3 inline-block">"SlayJ"</span>
          </motion.h1>
        </div>
      </>
    ),
  },
  {
    id: "creds-intro",
    duration: 2200,
    bg: "#0A0A0A",
    render: () => (
      <div className="flex flex-col justify-center w-full h-full">
        <motion.p initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }} className="text-[#FF1B91] uppercase tracking-[0.4em] text-sm font-bold mb-8">
          / Receipts only
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter" style={{ fontSize: "150px" }}>
          Trained.<br />
          <span className="text-[#FF1B91]">Insured.</span><br />
          Certified.
        </motion.h2>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.55 }} className="mt-10 text-white/65 text-2xl leading-snug">
          No shortcuts. No vibes only.<br />The receipts ↓
        </motion.p>
      </div>
    ),
  },
  {
    id: "certs",
    duration: 3000,
    bg: "#0A0A0A",
    render: () => (
      <div className="flex flex-col justify-center w-full h-full">
        <motion.p initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} className="text-[#FF1B91] uppercase tracking-[0.4em] text-sm font-bold mb-8">
          / Credentials
        </motion.p>
        <div className="grid grid-cols-2 gap-4">
          {[
            { p: "NASM", s: "CPT", line: "Certified Personal Trainer" },
            { p: "NFPT", s: "CPT", line: "Pro Trainer" },
            { p: "NASM", s: "CNC", line: "Nutrition Coach" },
            { p: "CPR", s: "/ AED", line: "Adult · Child · Infant" },
          ].map((c, i) => (
            <motion.div
              key={c.p + c.s}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 + i * 0.15 }}
              className="border border-[#FF1B91]/40 bg-[#FF1B91]/5 p-6"
            >
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-display font-black text-white text-4xl tracking-tighter leading-none">{c.p}</span>
                <span className="font-brush text-[#FF1B91] text-2xl leading-none">{c.s}</span>
              </div>
              <p className="text-white/55 text-base leading-snug">{c.line}</p>
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.95 }}
          className="mt-4 border-2 border-[#FF1B91] bg-[#FF1B91]/10 p-5 text-center"
        >
          <p className="text-[#FF1B91] text-xs uppercase tracking-[0.3em] font-bold mb-1">+ liability insured</p>
          <p className="font-display font-black text-white text-3xl tracking-tighter">ERGO Next · Fully covered</p>
        </motion.div>
      </div>
    ),
  },
  {
    id: "story",
    duration: 2400,
    bg: "#0A0A0A",
    full: true,
    render: () => (
      <>
        <BgPhoto src={PHOTOS.about} kenBurns="out" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/55 to-black/20" />
        <div className="absolute inset-0 flex flex-col justify-end pb-[640px] px-16">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="text-[#FF1B91] uppercase tracking-[0.4em] text-sm font-bold mb-5">
            / Background
          </motion.p>
          <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter" style={{ fontSize: "128px" }}>
            Ex D1 athlete.<br />
            <span className="text-[#FF1B91]">Ex corporate gym.</span><br />
            Now your coach.
          </motion.h2>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.55 }} className="mt-6 text-white/75 text-xl">
            BBA, Iona College. Built it. Burned out on the corporate floor. Doing it my way now.
          </motion.p>
        </div>
      </>
    ),
  },
  {
    id: "philosophy",
    duration: 2200,
    bg: "#0A0A0A",
    full: true,
    render: () => (
      <>
        <BgPhoto src={PHOTOS.social} kenBurns="in" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 flex flex-col items-center justify-center px-16 text-center">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="text-[#FF1B91] uppercase tracking-[0.5em] text-sm font-bold mb-8">
            / The mantra
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="font-display font-black uppercase text-white leading-[0.85] tracking-tighter"
            style={{ fontSize: "140px" }}
          >
            Obsessed with<br />
            <span className="font-brush font-normal normal-case text-[#FF1B91] -rotate-2 inline-block" style={{ fontSize: "180px" }}>watching you</span><br />
            win.
          </motion.h2>
        </div>
      </>
    ),
  },
  {
    id: "cta",
    duration: 2800,
    bg: "#FF1B91",
    render: () => (
      <div className="flex flex-col items-center justify-center w-full h-full text-center">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} className="text-white/85 uppercase tracking-[0.5em] text-sm font-bold mb-10">
          / Your move
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
          className="font-display font-black uppercase text-white leading-[0.85] tracking-tighter"
          style={{ fontSize: "210px" }}
        >
          Let's<br />
          <span className="font-brush font-normal normal-case -rotate-3 inline-block" style={{ fontSize: "230px" }}>get</span><br />
          to it.
        </motion.h2>
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.65 }} className="mt-10 text-white text-4xl font-bold tracking-tight">
          sweatwithslay.com
        </motion.p>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.95 }} className="mt-3 text-white/85 uppercase tracking-[0.4em] text-base font-semibold">
          @sweatwithslay
        </motion.p>
      </div>
    ),
  },
];

const TOTAL = SLIDES.reduce((acc, s) => acc + s.duration, 0);

export default function Promo2() {
  const [idx, setIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [ready, setReady] = useState(false);

  // Wait for all photo assets to load before starting the slide timer.
  useEffect(() => {
    const imgs = Array.from(document.images);
    if (imgs.length === 0) { setReady(true); return; }
    const pending = imgs.filter((i) => !(i.complete && i.naturalHeight > 0));
    if (pending.length === 0) { setReady(true); return; }
    let left = pending.length;
    const done = () => { left -= 1; if (left <= 0) setReady(true); };
    pending.forEach((i) => { i.addEventListener("load", done, { once: true }); i.addEventListener("error", done, { once: true }); });
    const fallback = setTimeout(() => setReady(true), 8000);
    return () => clearTimeout(fallback);
  }, []);

  useEffect(() => {
    if (!ready) return;
    document.body.setAttribute("data-promo-started", "true");
    const start = Date.now();
    let raf;
    const tick = () => {
      const elapsed = Date.now() - start;
      let total = 0;
      let active = 0;
      for (let i = 0; i < SLIDES.length; i++) {
        total += SLIDES[i].duration;
        if (elapsed < total) { active = i; break; }
        active = i;
      }
      setIdx(active);
      setProgress(Math.min(1, elapsed / TOTAL));
      if (elapsed < TOTAL) raf = requestAnimationFrame(tick);
      else document.body.setAttribute("data-promo-done", "true");
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [ready]);

  const slide = SLIDES[idx];

  return (
    <main
      data-testid="promo-page"
      data-promo-active={slide.id}
      className="font-body overflow-hidden fixed inset-0"
      style={{ background: slide.bg, color: "#fff" }}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-overlay z-20"
        style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")" }}
      />

      <AnimatePresence>
        <motion.div
          key={slide.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className={slide.full ? "absolute inset-0" : "absolute left-0 right-0 px-16"}
          style={slide.full ? {} : { top: SAFE_TOP, bottom: SAFE_BOTTOM }}
        >
          {slide.render()}
        </motion.div>
      </AnimatePresence>

      <div className="absolute left-12 right-12 h-1 bg-white/15 z-30" style={{ top: SAFE_TOP - 30 }}>
        <div className="h-full bg-white" style={{ width: `${progress * 100}%`, transition: "width 80ms linear" }} />
      </div>

      <div
        className="absolute left-0 right-0 text-center text-white/75 text-base uppercase tracking-[0.4em] font-semibold z-30"
        style={{ bottom: SAFE_BOTTOM - 60 }}
      >
        sweatwithslay.com
      </div>
    </main>
  );
}
